import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './CostAnalysis.css';
import { UserSession, normalizeSessions, toMs } from '../utils/sessionUtils';
import { apiClient, PLAYGROUND } from '../services/apiClient';
import { getUserDetailsByIds, UserDetailsListItem } from '../services/user';

type UserRow = {
  _id: string;
  name?: string;
  email?: string;
  role?: string;
  grade?: string;
  entityState?: string;
  createdAt?: number | string;
  sessionCount?: number;
  validSessionCount?: number;
};

type OrgRecord = {
  _id: string;
  title?: string;
  entityState?: string;
  createdAt?: number | string;
  updatedAt?: number | string;
  featureFlags?: Record<string, any>;
};

type OrgMembership = {
  orgId?: string;
  role?: string;
  status?: string;
  joinedAt?: number | string;
};

type OrgUser = UserRow & {
  memberships?: OrgMembership[];
  entityState?: string;
};

type ConceptTag = {
  topicId?: string;
  topicName: string;
  depthRequired: number | null;
};

type ConceptGradeLookup = {
  byId: Map<string, number | string | null | undefined>;
  byName: Map<string, number | string | null | undefined>;
};


type TrackUserActivityProps = {
  environment?: string;
};

const USERS_PAGE_SIZE = 25;
const ORGS_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const buildConceptGradeLookup = (grades: any): ConceptGradeLookup => {
  const lookup: ConceptGradeLookup = {
    byId: new Map(),
    byName: new Map(),
  };
  if (!Array.isArray(grades)) {
    return lookup;
  }
  grades.forEach((entry) => {
    if (!entry) return;
    const rawId =
      typeof entry.conceptId === 'string'
        ? entry.conceptId
        : entry.conceptId != null
          ? String(entry.conceptId)
          : '';
    const conceptId = rawId.trim();
    if (conceptId) {
      lookup.byId.set(conceptId, entry.grade);
    }
    const conceptName =
      typeof entry.conceptName === 'string' ? entry.conceptName.trim().toLowerCase() : '';
    if (conceptName) {
      lookup.byName.set(conceptName, entry.grade);
    }
  });
  return lookup;
};

const getConceptGradeValue = (concept: ConceptTag, lookup: ConceptGradeLookup) => {
  if (!concept) return undefined;
  const trimmedId = concept.topicId?.trim();
  const normalizedName = typeof concept.topicName === 'string' ? concept.topicName.trim().toLowerCase() : '';
  return (
    (trimmedId && lookup.byId.get(trimmedId)) ||
    (normalizedName && lookup.byName.get(normalizedName))
  );
};

const formatConceptGrade = (gradeValue: unknown) => {
  if (typeof gradeValue === 'number' && Number.isFinite(gradeValue)) return String(gradeValue);
  if (typeof gradeValue === 'string' && gradeValue.trim()) return gradeValue.trim();
  return '-';
};

/**
 * Enrich a list of users by fetching decrypted name/email from platform-node.
 * Uses Promise.allSettled so partial failures don't break the batch.
 * Falls back to original (possibly encrypted) values on failure.
 */
const enrichUsersWithDecryptedPII = async <T extends { _id: string; name?: string; email?: string }>(
  users: T[]
): Promise<T[]> => {
  if (!users.length) return users;
  console.log('[enrichUsers] Starting enrichment for', users.length, 'users');

  const userIds = users.map((u) => u._id);
  const detailsList = await getUserDetailsByIds(userIds);

  const detailsMap = new Map<string, UserDetailsListItem>();
  for (const d of detailsList) {
    detailsMap.set(d._id, d);
  }

  let enrichedCount = 0;
  const enriched = users.map((user) => {
    const details = detailsMap.get(user._id);
    if (details) {
      enrichedCount++;
      return {
        ...user,
        name: details.name || user.name,
        email: details.email || user.email,
      };
    }
    console.warn('[enrichUsers] No data returned for user', user._id);
    return user;
  });

  console.log(`[enrichUsers] Done: ${enrichedCount} enriched, ${users.length - enrichedCount} not found`);
  return enriched;
};

const formatAttemptDateForCsv = (timestamp?: number) => {
  if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) return '';
  try {
    return new Date(timestamp).toISOString();
  } catch {
    return '';
  }
};

const toCsvCell = (value: string) => {
  const safe = value.replace(/"/g, '""');
  return `"${safe}"`;
};

const normalizeUserRows = (data: any[]): UserRow[] =>
  Array.isArray(data)
    ? data
        .map((user: any) => {
          const id = user?._id ? String(user._id) : '';
          if (!id) return null;
          return { ...user, _id: id } as UserRow;
        })
        .filter((user): user is UserRow => Boolean(user))
    : [];

const normalizeOrgUsers = (data: any[]): OrgUser[] =>
  Array.isArray(data)
    ? data
        .map((user: any) => {
          const id = user?._id ? String(user._id) : '';
          if (!id) return null;
          return { ...user, _id: id } as OrgUser;
        })
        .filter((user): user is OrgUser => Boolean(user))
    : [];

const PaginationControls: React.FC<{
  page: number;
  total: number;
  limit: number;
  loading: boolean;
  onPageChange: (page: number) => void;
}> = ({ page, total, limit, loading, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  if (total <= limit) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '12px 0', fontSize: 13 }}>
      <button
        type="button"
        className="filter-btn"
        disabled={page <= 1 || loading}
        onClick={() => onPageChange(page - 1)}
        style={{ fontSize: '12px', padding: '4px 10px' }}
      >
        Prev
      </button>
      <span style={{ color: '#4b5563' }}>
        Page {page} of {totalPages} ({total} total)
      </span>
      <button
        type="button"
        className="filter-btn"
        disabled={page >= totalPages || loading}
        onClick={() => onPageChange(page + 1)}
        style={{ fontSize: '12px', padding: '4px 10px' }}
      >
        Next
      </button>
    </div>
  );
};

const TrackUserActivity: React.FC<TrackUserActivityProps> = ({ environment }) => {
  const prevEnvironmentRef = useRef<string | undefined>(undefined);
  // Org list state
  const [orgs, setOrgs] = useState<OrgRecord[]>([]);
  const [orgsTotal, setOrgsTotal] = useState(0);
  const [orgsPage, setOrgsPage] = useState(1);
  const [orgsLoading, setOrgsLoading] = useState<boolean>(false);
  const [orgsError, setOrgsError] = useState<string | null>(null);
  const [orgSearch, setOrgSearch] = useState<string>('');
  // User list state
  const [userSearch, setUserSearch] = useState<string>('');
  const [viewMode, setViewMode] = useState<'user' | 'org'>('user');
  const [allUsers, setAllUsers] = useState<UserRow[]>([]);
  const [allUsersTotal, setAllUsersTotal] = useState(0);
  const [allUsersPage, setAllUsersPage] = useState(1);
  const [allUsersLoading, setAllUsersLoading] = useState<boolean>(false);
  const [allUsersError, setAllUsersError] = useState<string | null>(null);
  // Expanded org users state (kept as full array — small per-org)
  const [expandedOrgId, setExpandedOrgId] = useState<string | null>(null);
  const [orgUsersMap, setOrgUsersMap] = useState<Record<string, OrgUser[]>>({});
  const [orgUsersLoadingMap, setOrgUsersLoadingMap] = useState<Record<string, boolean>>({});
  const [orgUsersErrorMap, setOrgUsersErrorMap] = useState<Record<string, string | null>>({});
  const [isExportingOrgCsv, setIsExportingOrgCsv] = useState(false);
  // No-org users state
  const [noOrgUsers, setNoOrgUsers] = useState<OrgUser[]>([]);
  const [noOrgTotal, setNoOrgTotal] = useState(0);
  const [noOrgPage, setNoOrgPage] = useState(1);
  const [noOrgLoading, setNoOrgLoading] = useState<boolean>(false);
  const [noOrgError, setNoOrgError] = useState<string | null>(null);
  const [noOrgExpanded, setNoOrgExpanded] = useState<boolean>(false);
  const [noOrgSearch, setNoOrgSearch] = useState<string>('');

  const navigate = useNavigate();
  const userSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const noOrgSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Data fetchers ---

  const fetchUsers = useCallback(async (page: number, search: string) => {
    setAllUsersLoading(true);
    setAllUsersError(null);
    try {
      const params = new URLSearchParams();
      params.set('state', 'ACTIVE');
      params.set('page', String(page));
      params.set('limit', String(USERS_PAGE_SIZE));
      const trimmed = search.trim();
      if (trimmed) params.set('search', trimmed);
      const data = await apiClient.get<any>(`/api/users?${params.toString()}`, PLAYGROUND);
      const users = normalizeUserRows(data?.users || []);
      const enriched = await enrichUsersWithDecryptedPII(users);
      setAllUsers(enriched);
      setAllUsersTotal(data?.total || 0);
      setAllUsersPage(data?.page || page);
    } catch (e: any) {
      setAllUsersError(e?.message || 'Failed to load users');
    } finally {
      setAllUsersLoading(false);
    }
  }, []);

  const fetchOrgs = useCallback(async (searchTerm: string, page: number) => {
    setOrgsLoading(true);
    setOrgsError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(ORGS_PAGE_SIZE));
      params.set('page', String(page));
      const trimmed = searchTerm.trim();
      if (trimmed) params.set('search', trimmed);
      const data = await apiClient.get<any>(`/api/orgs?${params.toString()}`, PLAYGROUND);
      const normalized: OrgRecord[] = Array.isArray(data?.orgs)
        ? data.orgs
            .map((org: any) => {
              const id = org?._id ? String(org._id) : '';
              if (!id) return null;
              return { ...org, _id: id } as OrgRecord;
            })
            .filter((org: OrgRecord | null): org is OrgRecord => Boolean(org))
        : [];
      setOrgs(normalized);
      setOrgsTotal(data?.total || 0);
      setOrgsPage(data?.page || page);
    } catch (e: any) {
      setOrgsError(e?.message || 'Failed to load organizations');
    } finally {
      setOrgsLoading(false);
    }
  }, []);

  const fetchOrgUsers = useCallback(async (orgId: string) => {
    if (!orgId) return;
    if (orgUsersMap[orgId] || orgUsersLoadingMap[orgId]) return;

    setOrgUsersLoadingMap(prev => ({ ...prev, [orgId]: true }));
    setOrgUsersErrorMap(prev => ({ ...prev, [orgId]: null }));
    try {
      const data = await apiClient.get<any>(`/api/orgs/${orgId}/users`, PLAYGROUND);
      const normalized = normalizeOrgUsers(Array.isArray(data) ? data : []);
      const enriched = await enrichUsersWithDecryptedPII(normalized);
      setOrgUsersMap(prev => ({ ...prev, [orgId]: enriched }));
    } catch (e: any) {
      setOrgUsersErrorMap(prev => ({ ...prev, [orgId]: e?.message || 'Failed to load organization users' }));
    } finally {
      setOrgUsersLoadingMap(prev => ({ ...prev, [orgId]: false }));
    }
  }, [orgUsersMap, orgUsersLoadingMap]);

  const fetchNoOrgUsers = useCallback(async (page: number, search: string) => {
    setNoOrgLoading(true);
    setNoOrgError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(USERS_PAGE_SIZE));
      const trimmed = search.trim();
      if (trimmed) params.set('search', trimmed);
      const data = await apiClient.get<any>(`/api/users/no-org?${params.toString()}`, PLAYGROUND);
      const normalized = normalizeOrgUsers(data?.users || []);
      const enriched = await enrichUsersWithDecryptedPII(normalized);
      setNoOrgUsers(enriched);
      setNoOrgTotal(data?.total || 0);
      setNoOrgPage(data?.page || page);
    } catch (e: any) {
      setNoOrgError(e?.message || 'Failed to load users without organization');
    } finally {
      setNoOrgLoading(false);
    }
  }, []);

  // --- Interactions ---

  const toggleOrg = useCallback(async (orgId: string) => {
    if (expandedOrgId === orgId) {
      setExpandedOrgId(null);
      return;
    }
    setExpandedOrgId(orgId);
    setNoOrgExpanded(false);
    await fetchOrgUsers(orgId);
  }, [expandedOrgId, fetchOrgUsers]);

  const toggleNoOrg = useCallback(async () => {
    if (noOrgExpanded) {
      setNoOrgExpanded(false);
      return;
    }
    setNoOrgExpanded(true);
    setExpandedOrgId(null);
    fetchNoOrgUsers(1, noOrgSearch);
  }, [noOrgExpanded, fetchNoOrgUsers, noOrgSearch]);

  // --- Environment change: reset everything ---
  useEffect(() => {
    const prevEnv = prevEnvironmentRef.current;
    if (prevEnv !== undefined && prevEnv !== environment) {
      console.log('[TrackUserActivity] Environment changed from', prevEnv, 'to', environment);
      setOrgs([]); setOrgsTotal(0); setOrgsPage(1);
      setExpandedOrgId(null);
      setOrgUsersMap({}); setOrgUsersLoadingMap({}); setOrgUsersErrorMap({});
      setOrgSearch(''); setUserSearch(''); setNoOrgSearch('');
      setOrgsError(null);
      setNoOrgUsers([]); setNoOrgTotal(0); setNoOrgPage(1);
      setNoOrgExpanded(false); setNoOrgError(null);
      setAllUsers([]); setAllUsersTotal(0); setAllUsersPage(1);
      setAllUsersError(null); setAllUsersLoading(false);
    }
    prevEnvironmentRef.current = environment;
  }, [environment]);

  // --- Auto-fetch on view mode switch ---
  useEffect(() => {
    if (viewMode === 'org' && orgs.length === 0 && !orgsLoading && !orgsError) {
      fetchOrgs('', 1);
    }
  }, [viewMode, orgs.length, orgsLoading, orgsError, fetchOrgs]);

  useEffect(() => {
    if (viewMode === 'user' && allUsers.length === 0 && !allUsersLoading && !allUsersError) {
      fetchUsers(1, '');
    }
  }, [viewMode, allUsers.length, allUsersLoading, allUsersError, fetchUsers]);

  // --- Debounced search for user view ---
  useEffect(() => {
    if (viewMode !== 'user') return;
    if (userSearchTimerRef.current) clearTimeout(userSearchTimerRef.current);
    userSearchTimerRef.current = setTimeout(() => {
      setAllUsersPage(1);
      fetchUsers(1, userSearch);
    }, SEARCH_DEBOUNCE_MS);
    return () => { if (userSearchTimerRef.current) clearTimeout(userSearchTimerRef.current); };
  }, [userSearch, viewMode, fetchUsers]);

  // --- Debounced search for no-org users ---
  useEffect(() => {
    if (!noOrgExpanded) return;
    if (noOrgSearchTimerRef.current) clearTimeout(noOrgSearchTimerRef.current);
    noOrgSearchTimerRef.current = setTimeout(() => {
      setNoOrgPage(1);
      fetchNoOrgUsers(1, noOrgSearch);
    }, SEARCH_DEBOUNCE_MS);
    return () => { if (noOrgSearchTimerRef.current) clearTimeout(noOrgSearchTimerRef.current); };
  }, [noOrgSearch, noOrgExpanded, fetchNoOrgUsers]);

  // --- Navigation ---

  const handleOrgUserNavigate = useCallback((userId: string) => {
    const targetId = String(userId || '').trim();
    if (!targetId) return;
    const params = new URLSearchParams();
    params.set('userId', targetId);
    if (environment) {
      params.set('env', environment);
    }
    navigate(`/user/attempts?${params.toString()}`);
  }, [navigate, environment]);

  // --- CSV export helpers (unchanged) ---

  const loadConceptsForQuestion = useCallback(
    async (questionId: string, cache: Map<string, ConceptTag[]>) => {
      const cached = cache.get(questionId);
      if (cached) return cached;
      try {
        const data = await apiClient.get<any>(`/api/questions/${encodeURIComponent(questionId)}/concepts`, PLAYGROUND);
        const normalized: ConceptTag[] = Array.isArray(data?.concepts)
          ? data.concepts
              .map((entry: any) => {
                if (!entry) return null;
                const topicName = typeof entry.topicName === 'string' ? entry.topicName.trim() : '';
                if (!topicName) return null;
                const depthRaw = entry.depthRequired;
                const depthNumber = depthRaw == null ? null : Number(depthRaw);
                const depthRequired = Number.isFinite(depthNumber) ? depthNumber : null;
                const normalizedTopicId = typeof entry.topicId === 'string' ? entry.topicId.trim() : '';
                return {
                  topicId: normalizedTopicId || undefined,
                  topicName,
                  depthRequired,
                } as ConceptTag;
              })
              .filter((entry: ConceptTag | null): entry is ConceptTag => entry !== null)
          : [];
        cache.set(questionId, normalized);
        return normalized;
      } catch (err) {
        console.error('[TrackUserActivity] Failed to load concepts for question', questionId, err);
        cache.set(questionId, []);
        return [];
      }
    },
    [],
  );

  const handleExportOrgCsv = useCallback(async (orgId: string) => {
    if (!orgId) {
      window.alert('Select an organization before exporting.');
      return;
    }
    const orgUsers = orgUsersMap[orgId] || [];
    if (!orgUsers.length) {
      window.alert('No users available in this organization to export.');
      return;
    }
    setIsExportingOrgCsv(true);
    try {
      const rows: string[][] = [];
      const conceptCache = new Map<string, ConceptTag[]>();
      for (const user of orgUsers) {
        const userId = user?._id?.trim();
        if (!userId) continue;
        let attempts: UserSession[] = [];
        try {
          const payload = await apiClient.get<any>(`/api/users/${userId}/attempts/details`, PLAYGROUND);
          const attemptsPayload = Array.isArray(payload?.attempts) ? payload.attempts : payload;
          attempts = normalizeSessions(attemptsPayload);
        } catch (err) {
          console.error('[TrackUserActivity] Error loading attempts for user', userId, err);
          continue;
        }
        for (const session of attempts) {
          const questionId = session.questionId?.trim();
          if (!questionId) continue;
          const concepts = await loadConceptsForQuestion(questionId, conceptCache);
          if (!concepts.length) continue;
          const gradeLookup = buildConceptGradeLookup(session?.attemptDetails?.fermiAnalysis?.conceptGrades);
          concepts.forEach((concept) => {
            const gradeValue = getConceptGradeValue(concept, gradeLookup);
            rows.push([
              user.name || user.email || 'Unnamed user',
              userId,
              session.attemptId,
              formatAttemptDateForCsv(session.attemptCreatedAt),
              session.questionUrl || '',
              session.solutionUrl || '',
              concept.topicName,
              formatConceptGrade(gradeValue),
              typeof concept.depthRequired === 'number' ? String(concept.depthRequired) : '',
            ]);
          });
        }
      }
      if (!rows.length) {
        window.alert('No attempt data available to export yet. Please try again later.');
        return;
      }
      const header = [
        'User Name',
        'User ID',
        'Attempt ID',
        'Attempt Date',
        'Question URL',
        'Solution URL',
        'Concept',
        'Concept Grade',
        'Concept Depth',
      ];
      const csvLines = [header, ...rows].map((columns) => columns.map((value) => toCsvCell(value || '')).join(','));
      const csvContent = csvLines.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const link = document.createElement('a');
      link.href = url;
      link.download = `org-${orgId}-attempts-${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[TrackUserActivity] Failed to export organization CSV', err);
      window.alert('Failed to export CSV. Please try again.');
    } finally {
      setIsExportingOrgCsv(false);
    }
  }, [orgUsersMap, loadConceptsForQuestion]);

  // --- Formatting helpers ---

  const formatTimestamp = (ms?: number) => {
    if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return '—';
    try {
      return new Date(ms).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (_) {
      return new Date(ms).toLocaleString();
    }
  };

  const getCreatedAtMs = (u: UserRow) => {
    const v: any = (u as any).createdAt;
    if (v == null) return 0;
    if (typeof v === 'number') return v;
    if (typeof v === 'string') {
      const n = Number(v);
      if (isFinite(n) && n > 0) return n;
      const d = Date.parse(v);
      return isNaN(d) ? 0 : d;
    }
    return 0;
  };

  // Client-side filter for org users within expanded org (small list, no server pagination)
  const filterOrgUsers = useCallback(<T extends UserRow>(users: T[], query: string): T[] => {
    if (!query.trim()) return users;
    const q = query.toLowerCase().trim();
    return users.filter((user) => {
      const name = (user.name || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      const role = (user.role || '').toLowerCase();
      const grade = (user.grade || '').toLowerCase();
      return name.includes(q) || email.includes(q) || role.includes(q) || grade.includes(q);
    });
  }, []);

  // --- Render: User View ---

  const renderUserView = () => {
    return (
      <div className="usage-panel">
        {allUsersLoading && <div className="dashboard-loading">Loading users...</div>}
        {allUsersError && <div className="dashboard-error">{allUsersError}</div>}
        {!allUsersLoading && !allUsersError && allUsers.length === 0 && (
          <div className="usage-empty-state">
            {userSearch.trim() ? 'No users match your search.' : 'No users found.'}
          </div>
        )}
        {!allUsersLoading && !allUsersError && allUsers.length > 0 && (
          <>
            <div className="usage-table-wrapper">
              <table className="usage-users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>User Created</th>
                    <th>Grade</th>
                    <th>Sessions</th>
                    <th>Valid Sessions</th>
                  </tr>
                </thead>
                <tbody>
                  {allUsers.map((user) => (
                    <tr key={user._id} onClick={() => handleOrgUserNavigate(user._id)}>
                      <td>{user.name || user.email || 'Unnamed user'}</td>
                      <td>{user.email || '—'}</td>
                      <td>{user.role || '—'}</td>
                      <td>{user.entityState || '—'}</td>
                      <td>{formatTimestamp(getCreatedAtMs(user))}</td>
                      <td>{user.grade || '—'}</td>
                      <td>{typeof user.sessionCount === 'number' ? user.sessionCount : '—'}</td>
                      <td>{typeof user.validSessionCount === 'number' ? user.validSessionCount : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls
              page={allUsersPage}
              total={allUsersTotal}
              limit={USERS_PAGE_SIZE}
              loading={allUsersLoading}
              onPageChange={(p) => { setAllUsersPage(p); fetchUsers(p, userSearch); }}
            />
          </>
        )}
      </div>
    );
  };

  // --- Render: Org View ---

  const renderOrgView = () => {
    return (
      <div className="usage-panel">
        {orgsLoading && <div className="dashboard-loading">Loading organizations...</div>}
        {orgsError && <div className="dashboard-error">{orgsError}</div>}
        {!orgsLoading && !orgsError && orgs.length === 0 && (
          <div className="usage-empty-state">No organizations found. Try refining your search.</div>
        )}
        {!orgsLoading && orgs.length > 0 && (
          <>
            <table>
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>State</th>
                  <th>Created</th>
                  <th>Updated</th>
                  <th>Actions</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {orgs.map((org) => {
                  const isExpanded = expandedOrgId === org._id;
                  const orgUsers = orgUsersMap[org._id] || [];
                  const filteredOrgUsers = filterOrgUsers(orgUsers, userSearch);
                  const isLoadingUsers = orgUsersLoadingMap[org._id] || false;
                  const usersError = orgUsersErrorMap[org._id];

                  return (
                    <React.Fragment key={org._id}>
                      <tr onClick={() => toggleOrg(org._id)} style={{ cursor: 'pointer' }}>
                        <td>{org.title || 'Unnamed organization'}</td>
                        <td>{org.entityState || '—'}</td>
                        <td>{formatTimestamp(toMs(org.createdAt))}</td>
                        <td>{formatTimestamp(toMs(org.updatedAt))}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="filter-btn"
                            onClick={() => handleExportOrgCsv(org._id)}
                            disabled={isExportingOrgCsv || orgUsers.length === 0}
                            style={{ fontSize: '11px', padding: '4px 8px' }}
                          >
                            {isExportingOrgCsv ? 'Exporting…' : 'Export CSV'}
                          </button>
                        </td>
                        <td>{isExpanded ? '▼' : '▶'}</td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={6}>
                            {isLoadingUsers && <div className="dashboard-loading">Loading users...</div>}
                            {usersError && <div className="dashboard-error">{usersError}</div>}
                            {!isLoadingUsers && !usersError && orgUsers.length === 0 && (
                              <div className="usage-empty-state">No users found for this organization.</div>
                            )}
                            {!isLoadingUsers && orgUsers.length > 0 && (
                              <div style={{ padding: '16px' }}>
                                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'center' }}>
                                  <input
                                    className="search-input"
                                    type="text"
                                    placeholder="Search users by name, email, role, or grade..."
                                    value={userSearch}
                                    onChange={(e) => setUserSearch(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ flex: 1 }}
                                  />
                                  {userSearch && (
                                    <button
                                      type="button"
                                      className="filter-btn"
                                      onClick={(e) => { e.stopPropagation(); setUserSearch(''); }}
                                      style={{ fontSize: '11px', padding: '4px 8px' }}
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>
                                {filteredOrgUsers.length === 0 ? (
                                  <div className="usage-empty-state">No users match your search.</div>
                                ) : (
                                  <div className="usage-table-wrapper">
                                    <table className="usage-users-table">
                                      <thead>
                                        <tr>
                                          <th>Name</th>
                                          <th>Email</th>
                                          <th>Org Role</th>
                                          <th>Status</th>
                                          <th>Joined</th>
                                          <th>User Created</th>
                                          <th>Grade</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {filteredOrgUsers.map((user) => {
                                          const membership = (user.memberships || []).find((m) => m?.orgId === org._id);
                                          return (
                                            <tr key={user._id} onClick={(e) => { e.stopPropagation(); handleOrgUserNavigate(user._id); }}>
                                              <td>{user.name || user.email || 'Unnamed user'}</td>
                                              <td>{user.email || '—'}</td>
                                              <td>{membership?.role || user.role || '—'}</td>
                                              <td>{membership?.status || user.entityState || '—'}</td>
                                              <td>{membership?.joinedAt ? formatTimestamp(toMs(membership.joinedAt)) : '—'}</td>
                                              <td>{formatTimestamp(getCreatedAtMs(user))}</td>
                                              <td>{user.grade || '—'}</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {/* Virtual "No Organization" row */}
                <React.Fragment key="no-org">
                  <tr onClick={toggleNoOrg} style={{ cursor: 'pointer', backgroundColor: '#f9fafb' }}>
                    <td><em>No Organization</em></td>
                    <td>—</td>
                    <td>—</td>
                    <td>—</td>
                    <td>—</td>
                    <td>{noOrgExpanded ? '▼' : '▶'}</td>
                  </tr>
                  {noOrgExpanded && (
                    <tr>
                      <td colSpan={6}>
                        {noOrgLoading && <div className="dashboard-loading">Loading users...</div>}
                        {noOrgError && <div className="dashboard-error">{noOrgError}</div>}
                        {!noOrgLoading && !noOrgError && noOrgUsers.length === 0 && (
                          <div className="usage-empty-state">
                            {noOrgSearch.trim() ? 'No users match your search.' : 'No users found without organization membership.'}
                          </div>
                        )}
                        {!noOrgLoading && (noOrgUsers.length > 0 || noOrgSearch.trim()) && (
                          <div style={{ padding: '16px' }}>
                            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'center' }}>
                              <input
                                className="search-input"
                                type="text"
                                placeholder="Search users by name or email..."
                                value={noOrgSearch}
                                onChange={(e) => setNoOrgSearch(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                style={{ flex: 1 }}
                              />
                              {noOrgSearch && (
                                <button
                                  type="button"
                                  className="filter-btn"
                                  onClick={(e) => { e.stopPropagation(); setNoOrgSearch(''); }}
                                  style={{ fontSize: '11px', padding: '4px 8px' }}
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                            {noOrgUsers.length > 0 && (
                              <>
                                <div className="usage-table-wrapper">
                                  <table className="usage-users-table">
                                    <thead>
                                      <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Role</th>
                                        <th>Status</th>
                                        <th>User Created</th>
                                        <th>Grade</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {noOrgUsers.map((user) => (
                                        <tr key={user._id} onClick={(e) => { e.stopPropagation(); handleOrgUserNavigate(user._id); }}>
                                          <td>{user.name || user.email || 'Unnamed user'}</td>
                                          <td>{user.email || '—'}</td>
                                          <td>{user.role || '—'}</td>
                                          <td>{user.entityState || '—'}</td>
                                          <td>{formatTimestamp(getCreatedAtMs(user))}</td>
                                          <td>{user.grade || '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                <PaginationControls
                                  page={noOrgPage}
                                  total={noOrgTotal}
                                  limit={USERS_PAGE_SIZE}
                                  loading={noOrgLoading}
                                  onPageChange={(p) => { setNoOrgPage(p); fetchNoOrgUsers(p, noOrgSearch); }}
                                />
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              </tbody>
            </table>
            <PaginationControls
              page={orgsPage}
              total={orgsTotal}
              limit={ORGS_PAGE_SIZE}
              loading={orgsLoading}
              onPageChange={(p) => { setOrgsPage(p); fetchOrgs(orgSearch, p); }}
            />
          </>
        )}
      </div>
    );
  };

  return (
    <div className="cost-dashboard" style={{ background: 'transparent' }}>
      <div className="dashboard-content">
        <div className="table-container">
          <div
            className="table-header"
            style={{ flexDirection: 'column', alignItems: 'stretch', gap: 16 }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h3>Overall User Usage</h3>
                <span style={{
                  padding: '4px 12px',
                  fontSize: '13px',
                  fontWeight: '600',
                  borderRadius: '5px',
                  backgroundColor: environment === 'local' ? '#fff3cd' : environment === 'staging' ? '#d1ecf1' : '#d4edda',
                  color: '#333',
                  border: '1px solid #ccc'
                }}>
                  {environment === 'local' ? '🟡 Local' : environment === 'staging' ? '🔵 Staging' : '🟢 Production'}
                </span>
              </div>
            </div>
            <div className="usage-controls">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  type="button"
                  className={`view-tab ${viewMode === 'user' ? 'active' : ''}`}
                  onClick={() => setViewMode('user')}
                >
                  All Users
                </button>
                <button
                  type="button"
                  className={`view-tab ${viewMode === 'org' ? 'active' : ''}`}
                  onClick={() => setViewMode('org')}
                >
                  By Org
                </button>
              </div>
              {viewMode === 'org' ? (
                <div className="usage-search-group">
                  <input
                    className="search-input"
                    type="text"
                    placeholder="Search org by title..."
                    value={orgSearch}
                    onChange={(e) => setOrgSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setOrgsPage(1); fetchOrgs(orgSearch, 1); } }}
                  />
                  <button
                    className="filter-btn"
                    onClick={() => { setOrgsPage(1); fetchOrgs(orgSearch, 1); }}
                    disabled={orgsLoading}
                  >
                    {orgsLoading ? 'Searching...' : 'Search Orgs'}
                  </button>
                  <button
                    className="filter-btn"
                    onClick={() => { setOrgSearch(''); setOrgsPage(1); fetchOrgs('', 1); }}
                    disabled={!orgSearch || orgsLoading}
                  >
                    Clear
                  </button>
                </div>
              ) : (
                <div className="usage-search-group">
                  <input
                    className="search-input"
                    type="text"
                    placeholder="Search users by name or email..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                  />
                  <button
                    className="filter-btn"
                    onClick={() => setUserSearch('')}
                    disabled={!userSearch}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>

          {viewMode === 'user' ? renderUserView() : renderOrgView()}
        </div>
      </div>
    </div>
  );
};

export default TrackUserActivity;
